import {
  Box,
  IconButton,
  Loader,
  Typography,
  useSwiperRef,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { ProjectsCarousel } from "@js/apps/freelancer/components/projects/projects-carousel";
import { ApplicationQAList } from "@js/apps/jobs/components/application-qa-list";
import { RequirementsIndicator } from "@js/apps/jobs/components/requirements-indicator";
import { BidSkillChip } from "@js/apps/skills/components";
import { AttachmentList } from "@js/components/attachment-list";
import type { WorkSampleItem } from "@js/types/freelancer";
import type { EmployerBid, EmployerBidListBid, Job } from "@js/types/jobs";

import { BidTabLoader } from "./bid-tab-loader";

export const BidDetailsApplicationTab = ({
  bid,
  job,
}: {
  bid: EmployerBidListBid | EmployerBid;
  job: Job;
}) => {
  if (!("portfolio_items" in bid.freelancer)) {
    return <BidTabLoader />;
  }

  const workSamples = bid.freelancer.portfolio_items;

  return (
    <Box>
      {job && (
        <RequirementsIndicator
          job={job}
          locationRequirementsMet={bid.location_requirement_met}
          timezoneRequirementsMet={bid.timezone_requirement_met}
        />
      )}

      <ApplicationQAList bid={bid} />

      <Box sx={{ mt: 6 }}>
        <AttachmentList attachments={bid.attachments} />
      </Box>

      {!!workSamples?.length && (
        <Box sx={{ mt: 6, maxWidth: "100%" }}>
          <PortfolioItemsSlider items={workSamples} />
        </Box>
      )}
      {!!bid.freelancer.skills?.length && (
        <>
          <Typography
            component="p"
            variant="paragraph"
            size="large"
            sx={{ mb: 2 }}
          >
            Talent skills
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {bid.freelancer.skills.map((item) => {
              return <BidSkillChip key={item.id} skill={item.skill} />;
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

const PortfolioItemsSlider = ({ items }: { items: WorkSampleItem[] }) => {
  const [nextEl, nextElRef] = useSwiperRef();
  const [prevEl, prevElRef] = useSwiperRef();

  if (!items) return <Loader />;
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography component="h5" variant="paragraph" size="large">
          Projects
        </Typography>

        <Box sx={{ display: "flex", columnGap: 1 }}>
          <IconButton
            variant="secondary"
            aria-label="previous"
            size="medium"
            ref={prevElRef}
          >
            <ArrowLeftIcon />
          </IconButton>
          <IconButton
            variant="secondary"
            aria-label="next"
            size="medium"
            ref={nextElRef}
          >
            <ArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <ProjectsCarousel
          items={items}
          itemWidth="95%"
          navigationNextRef={nextEl}
          navigationPrevRef={prevEl}
        />
      </Box>
    </Box>
  );
};
